"use client";

import { DateTime } from "luxon";

import { USHV_ORION_MAPPINGS } from "@/configuration/groups/ushv-orion/document-mappings";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent, CopyIcon } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/src/components/shadcn/ui/table";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { usePriorAuthorizationsByReferralId } from "@/src/lib/data-fetchers/prior-authorization-fetchers";
import { usePatientAppointments } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { renderTemplate } from "@/src/lib/templating";
import { formatDateTimeWithTimezoneForDisplay, formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import { Classification } from "@/src/models/constants/classifications";
import { getCoverageDisplayName } from "@/src/models/coverage";
import { getDiagnosisCodeDisplayName, isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { getProviderDisplayName } from "@/src/models/practitioner";

const USHV_ORION_DISPLAY_TIMEZONE = "America/Chicago";
const ORION_REFERRALS_QUEUE_NAME = "Orion, Referrals";
const USHV_IT_EMAIL = "emrsupport@usheartandvascular.com";

function UshvOrionReferralInstructionsContent({
  referral,
  taskId,
  assignee,
}: {
  referral: Referral;
  taskId: string;
  assignee: string;
}) {
  const { referralLocation: faxReferralLocation, authLocation: faxAuthLocation } = useFaxLocationAssignment(taskId);
  const { appointments, isPending: isAppointmentsPending } = usePatientAppointments(referral.patient?.id);
  const { priorAuthorizations } = usePriorAuthorizationsByReferralId(referral.id ?? null);

  const startOfDay = DateTime.now().setZone(USHV_ORION_DISPLAY_TIMEZONE).startOf("day");
  const upcomingAppointments = appointments?.filter((appointment) => {
    const appointmentDate = DateTime.fromISO(appointment.appointmentDatetime || "").setZone(
      USHV_ORION_DISPLAY_TIMEZONE
    );

    return appointmentDate.isValid && appointmentDate >= startOfDay;
  });

  const referralMapping = USHV_ORION_MAPPINGS[Classification.REFERRAL];
  const documentLabel = referralMapping
    ? renderTemplate(referralMapping.documentLabel, (referral as unknown as Record<string, unknown>) || "", {
        subdomain: ConfigSubdomain.USHV_ORION,
      })
    : "";
  const referralFolder = referralMapping?.folder || "Referral Documents";

  const relevantPriorAuth = priorAuthorizations.find((priorAuth) => priorAuth.startDate || priorAuth.endDate) ?? null;
  const priority = isStatOrUrgentPriority(referral.schedulingPriority) ? "High" : "Normal";

  const lineOfBusinessDisplay = referral.primaryCoverage?.lineOfBusiness || null;
  const coverageDisplay = referral.primaryCoverage
    ? getCoverageDisplayName({ coverage: referral.primaryCoverage, includeSubscriberName: false })
    : null;
  const insuranceDisplay = [coverageDisplay, lineOfBusinessDisplay].filter(Boolean).join(" ") || null;

  const referringProviderName = referral.referringProvider
    ? getProviderDisplayName(referral.referringProvider)
    : "unspecified";
  const receivingProviderName = referral.receivingProvider
    ? getProviderDisplayName(referral.receivingProvider)
    : "unspecified";

  const diagnosisList = Array.isArray(referral.diagnosisList) ? referral.diagnosisList : [];
  const diagnosisDisplay =
    diagnosisList.length > 0
      ? diagnosisList.map((diagnosis) => getDiagnosisCodeDisplayName(diagnosis)).join(", ")
      : referral.diagnosisCode || referral.diagnosisText
        ? getDiagnosisCodeDisplayName({
            diagnosisCode: referral.diagnosisCode,
            diagnosisText: referral.diagnosisText,
            diagnosisCodeType: referral.diagnosisCodeType,
          })
        : null;

  const authNumber = referral.authorizationNumber || null;
  const authInfoParts = [
    authNumber ? `Auth number: ${authNumber}` : null,
    relevantPriorAuth?.startDate ? `Start date: ${formatIsoDate(relevantPriorAuth.startDate)}` : null,
    relevantPriorAuth?.endDate ? `End date: ${formatIsoDate(relevantPriorAuth.endDate)}` : null,
    referral.numberOfVisits != null ? `Visits: ${referral.numberOfVisits}` : null,
  ].filter((part): part is string => Boolean(part));

  const ecwActionSubject = `Referral${lineOfBusinessDisplay ? ` - ${lineOfBusinessDisplay}` : ""}${
    authNumber ? ` - Authorization number: ${authNumber}` : ""
  }`;

  const ecwActionNotes = `Patient referred by ${referringProviderName} to ${receivingProviderName} for ${
    referral.reasonForReferral || "unspecified reason"
  }${diagnosisDisplay ? `, ${diagnosisDisplay}` : ""}.${
    insuranceDisplay ? ` Insurance is ${insuranceDisplay}.` : ""
  }${authInfoParts.length > 0 ? `\n${authInfoParts.join(" ")}` : ""}`;

  const referringProviderMissingEmailTemplate = `Hello, could you please add the following referring provider to eCW for this patient's referral?

Provider First Name: ${referral.referringProvider?.givenName || "N/A"}
Provider Last Name: ${referral.referringProvider?.familyName || "N/A"}
Phone number: ${referral.referringLocation?.phoneNumber || referral.referringProvider?.npiRegistryEntry?.addresses?.[0]?.telephone_number || "N/A"}
Fax number: ${referral.referringLocation?.faxPhoneNumber || referral.referringProvider?.npiRegistryEntry?.addresses?.[0]?.fax_number || "N/A"}
NPI number: ${referral.referringProvider?.npiNumber || "N/A"}

Please let me know if you need anything else from me, thank you so much!

Best, [ your name ]`;

  return (
    <div className="flex flex-col gap-4">
      <span className="font-medium text-center">{"USHV Orion"} Referral Instructions:</span>
      {documentLabel && (
        <div>
          <span className="font-semibold text-foreground">Document Label:</span>{" "}
          <CopyableContent text={documentLabel} />
        </div>
      )}
      <div className="text-muted-foreground space-y-4">
        {/* Referral Workflow - automated by webecw, shown here for verification */}
        <div>
          <span className="font-semibold">Referral Workflow (automated):</span>
          <ol className="mt-2 ml-4 list-decimal list-inside space-y-1">
            <li>
              File is automatically uploaded to the <span className="font-medium">{referralFolder}</span> folder in eCW,
              assigned to <CopyableContent text={assignee} />
            </li>
          </ol>
        </div>

        {/* Add eCW Action - manual step */}
        <div>
          <span className="font-semibold">Add eCW Action (manual):</span>
          <div className="mt-2 ml-4 flex flex-col gap-2">
            <span>
              Manually create an eCW Action for the <CopyableContent text={ORION_REFERRALS_QUEUE_NAME} /> queue using
              the following:
            </span>
            <div className="p-3 border rounded-md bg-muted/50 text-sm flex flex-col gap-2">
              <div>
                Subject: <CopyableContent text={ecwActionSubject} />
              </div>
              <div>Priority: {priority}</div>
              <div>Assigned To: {ORION_REFERRALS_QUEUE_NAME}</div>
              <div className="flex flex-col gap-1">
                <span>Notes:</span>
                <CopyableContent text={ecwActionNotes} textClassName="whitespace-pre-wrap" />
              </div>
            </div>
          </div>
        </div>

        {/* Add Referring Provider */}
        <div>
          <span className="font-semibold">If Referring Provider is Missing:</span>
          <div className="mt-2 ml-4 flex flex-col gap-2">
            <span>
              Send the following email to <CopyableContent text={USHV_IT_EMAIL} />:
            </span>
            <div className="p-3 border rounded-md bg-muted/50 text-sm whitespace-pre-wrap relative group">
              {referringProviderMissingEmailTemplate}
              <CopyIcon text={referringProviderMissingEmailTemplate} />
            </div>
          </div>
        </div>

        {/* Add Insurance - only show if coverage is present on referral */}
        {(referral.primaryCoverage || referral.secondaryCoverage) && (
          <div>
            <span className="font-semibold">Add insurance:</span>
            <div className="ml-4 mt-1 text-muted-foreground">
              <span>Insurance in referral has not been added to patient chart. Please add insurance.</span>
            </div>
          </div>
        )}
      </div>
      <div className="space-y-1">
        <div>
          <span className="font-semibold text-foreground">Referral Location:</span>{" "}
          <CopyableContent text={faxReferralLocation || "N/A"} />
        </div>
        <div>
          <span className="font-semibold text-foreground">Auth Location:</span>{" "}
          <CopyableContent text={faxAuthLocation || "N/A"} />
        </div>
      </div>

      {/* Future Encounters */}
      <div>
        <span className="font-semibold text-foreground">Future Encounters:</span>
        {isAppointmentsPending ? (
          <span className="ml-2 text-xs text-muted-foreground">Checking for upcoming encounters...</span>
        ) : upcomingAppointments?.length ? (
          <Table className="mt-2 text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingAppointments.map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell>
                    {formatDateTimeWithTimezoneForDisplay(
                      appointment.appointmentDatetime,
                      USHV_ORION_DISPLAY_TIMEZONE,
                      false
                    )}
                  </TableCell>
                  <TableCell>{appointment.ehrType || "N/A"}</TableCell>
                  <TableCell>{appointment.ehrReason || "N/A"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <span className="ml-2 text-xs text-muted-foreground">No upcoming encounters found</span>
        )}
      </div>
    </div>
  );
}

interface UshvOrionReferralInstructionsProps {
  referral: Referral;
  taskId: string;
  assignee: string;
}

export function UshvOrionReferralInstructions({ referral, taskId, assignee }: UshvOrionReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <UshvOrionReferralInstructionsContent referral={referral} taskId={taskId} assignee={assignee} />
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
